/* eslint react-hooks/exhaustive-deps: 0 */
import { asArray } from "@/lib/asArray";
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function W173AnalyticalPage() {
  const { current, currentEngId } = useEngagement();
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const reload = async () => {
    if (!currentEngId) return;
    const r = await api.get(`/engagements/${currentEngId}/w17/analytical`, { _silent: true });
    setState(r.data);
  };
  useEffect(() => { reload(); }, [currentEngId]);
  const saveRow = async (fs_head, patch) => {
    try {
      const r = await api.post(`/engagements/${currentEngId}/w17/analytical/row`,
                                     { fs_head, patch }, { _silent: true });
      setState(r.data); toast.success(t("w17_3.rowSaved"));
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };
  const setCoverage = async (area_id, fs_head) => {
    try {
      const r = await api.post(`/engagements/${currentEngId}/w17/analytical/coverage`,
                                     { area_id, fs_head }, { _silent: true });
      setState(r.data);
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };
  const runRecompute = async () => {
    try {
      await api.post(`/engagements/${currentEngId}/w17/analytical/run`, {}, { _silent: true });
      toast.success(t("w17_3.runQueued"));
      setTimeout(reload, 1500);
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };
  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;
  if (!state) return <div className="text-sm text-muted-foreground p-6" data-testid="w17-3-loading">{t("common.loading")}</div>;
  const isReview = (current?.engagement_type || "audit") === "review";
  if (!isReview) return <div className="p-6 text-sm text-muted-foreground" data-testid="w17-3-audit-only">{t("w17_3.notForAudit")}</div>;
  return (
    <div className="space-y-6" data-testid="w17-3-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="w17-3-title">
          <span dir="ltr" className="font-mono text-[13px] text-muted-foreground me-2">503(r)</span>
          {t("w17_3.title")}
        </h1>
        <SignOffBar itemType="w17_3" itemKey="W17.3" />   {/* Batch 11 · A2 */}
        <p className="text-sm text-muted-foreground">{t("w17_3.subtitle")}</p>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base normal-case">{t("w17_3.coverageTitle")}</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-[12px]">
          {(state.likely_areas || []).length === 0 && (
            <div className="text-muted-foreground" data-testid="w17-3-no-likely">{t("w17_3.noLikelyAreas")}</div>
          )}
          {asArray(state.likely_areas).map(a => (
            <div key={a.id} className="flex items-center gap-2" data-testid={`w17-3-cov-${a.id}`}>
              <div className="flex-1">{a.text}</div>
              <select value={a.covered_by_fs_head || ""} onChange={e => setCoverage(a.id, e.target.value)}
                       className="h-7 rounded border px-1 text-[11px]"
                       data-testid={`w17-3-cov-${a.id}-sel`}>
                <option value="">{t("w17_3.pickHead")}</option>
                {asArray(state.rows).map(r => <option key={r.fs_head} value={r.fs_head}>{r.fs_head}</option>)}
              </select>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base normal-case">{t("w17_3.rowsTitle")}</CardTitle>
          <Button size="sm" variant="outline" onClick={runRecompute} data-testid="w17-3-run">{t("w17_3.runBtn")}</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {asArray(state.rows).map(r => <RowCard key={r.fs_head} row={r} save={saveRow} t={t} />)}
        </CardContent>
      </Card>
    </div>
  );
}

function RowCard({ row, save, t }) {
  const [f, setF] = React.useState(row);
  React.useEffect(() => { setF(row); }, [row.id]);
  const diff = Number(f.actual || 0) - Number(f.expectation || 0);
  const above = f.threshold > 0 && Math.abs(diff) > Number(f.threshold);
  return (
    <div className="rounded border p-2 text-[12px]" data-testid={`w17-3-row-${f.fs_head}`}>
      <div className="mb-1 font-medium">{f.fs_head} · {t("w17_3.cyBalance")}: {Number(f.cy_balance).toFixed(2)}</div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <Input placeholder={t("w17_3.expectationPh")} value={f.expectation || ""} onChange={e => setF({ ...f, expectation: e.target.value })} data-testid={`w17-3-row-${f.fs_head}-exp`} />
        <Input placeholder={t("w17_3.actualPh")} value={f.actual || ""} onChange={e => setF({ ...f, actual: e.target.value })} data-testid={`w17-3-row-${f.fs_head}-act`} />
        <Input placeholder={t("w17_3.thresholdPh")} value={f.threshold || ""} onChange={e => setF({ ...f, threshold: e.target.value })} data-testid={`w17-3-row-${f.fs_head}-thr`} />
        <Input placeholder={t("w17_3.diffPh")} value={diff.toFixed(2)} readOnly data-testid={`w17-3-row-${f.fs_head}-diff`} />
      </div>
      <Textarea placeholder={t("w17_3.plannedInquiriesPh")} value={f.planned_inquiries || ""} onChange={e => setF({ ...f, planned_inquiries: e.target.value })} className="mt-2 h-16 text-[11px]" data-testid={`w17-3-row-${f.fs_head}-inq`} />
      <Textarea placeholder={t("w17_3.explanationPh")} value={f.explanation || ""} onChange={e => setF({ ...f, explanation: e.target.value })} className="mt-2 h-16 text-[11px]" data-testid={`w17-3-row-${f.fs_head}-exp2`} />
      <Textarea placeholder={t("w17_3.corroborationPh")} value={f.corroboration || ""} onChange={e => setF({ ...f, corroboration: e.target.value })} className="mt-2 h-16 text-[11px]" data-testid={`w17-3-row-${f.fs_head}-corr`} />
      <Textarea placeholder={t("w17_3.conclusionPh")} value={f.conclusion || ""} onChange={e => setF({ ...f, conclusion: e.target.value })} className="mt-2 h-16 text-[11px]" data-testid={`w17-3-row-${f.fs_head}-conc`} />
      {above && !(f.explanation || "").trim() && (
        <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900" data-testid={`w17-3-row-${f.fs_head}-warn`}>
          {t("w17_3.diffAboveThreshold")}
        </div>
      )}
      <Button size="sm" className="mt-2" onClick={() => save(f.fs_head, f)} data-testid={`w17-3-row-${f.fs_head}-save`}>{t("w17_3.save")}</Button>
    </div>
  );
}
